import { api } from '@/plugins/axios.js'

/**
 * Serviço responsável por carregar imagens protegidas
 * pela autenticação JWT.
 */
class ArquivosService {
  constructor() {
    this.cacheFotos = new Map()
    this.requisicoesPendentes = new Map()
    this.versoesCache = new Map()
    this.listenersFotoPerfil = new Set()
  }

  /**
   * Busca uma imagem privada na API.
   *
   * @param {string|number} codIbge
   * @param {string} tipo
   * @returns {Promise<string|null>}
   */
  async obterImagem(codIbge, tipo) {
    if (!codIbge) {
      return null
    }

    const tiposPermitidos = ['brasao', 'logotipo', 'foto-perfil']

    if (!tiposPermitidos.includes(tipo)) {
      throw new Error('Tipo de imagem inválido.')
    }

    const response = await api.get(`/app/arquivos/${codIbge}/${tipo}`, {
      responseType: 'blob',
    })

    if (response.status === 204) {
      return null
    }

    const blob = response.data

    if (!(blob instanceof Blob) || blob.size === 0) {
      return null
    }

    if (!blob.type.startsWith('image/')) {
      throw new Error('O arquivo recebido não é uma imagem.')
    }

    return URL.createObjectURL(blob)
  }

  /**
   * Retorna a foto de perfil ou um avatar padrão.
   *
   * Quando usarAvatarPadrao for true, retorna diretamente
   * o avatar do DiceBear, sem consultar a API.
   *
   * @param {string|number} codIbge
   * @param {string|number} usuarioId
   * @param {boolean} usarAvatarPadrao
   * @returns {Promise<string|null>}
   */
  async obterFotoPerfil(codIbge, usuarioId, usarAvatarPadrao = false) {
    if (usarAvatarPadrao) {
      return this.obterAvatarPadrao()
    }

    if (!codIbge || !usuarioId) {
      return null
    }

    const chave = `${usuarioId}:${codIbge}`

    if (this.cacheFotos.has(chave)) {
      return this.cacheFotos.get(chave)
    }

    if (this.requisicoesPendentes.has(chave)) {
      return this.requisicoesPendentes.get(chave)
    }

    const versao = this.versoesCache.get(chave) || 0

    const requisicao = this.obterImagem(codIbge, 'foto-perfil')
      .then((url) => {
        if ((this.versoesCache.get(chave) || 0) !== versao) {
          this.liberarImagem(url)
          return null
        }

        this.cacheFotos.set(chave, url)
        return url
      })
      .finally(() => {
        if (this.requisicoesPendentes.get(chave) === requisicao) {
          this.requisicoesPendentes.delete(chave)
        }
      })

    this.requisicoesPendentes.set(chave, requisicao)

    return requisicao
  }

  /**
   * Invalida a foto de perfil armazenada em cache.
   *
   * @param {string|number} codIbge
   * @param {string|number} usuarioId
   * @returns {void}
   */
  invalidarFotoPerfil(codIbge, usuarioId) {
    const chave = `${usuarioId}:${codIbge}`

    this.versoesCache.set(chave, (this.versoesCache.get(chave) || 0) + 1)

    this.liberarImagem(this.cacheFotos.get(chave))

    this.cacheFotos.delete(chave)
    this.requisicoesPendentes.delete(chave)

    this.notificarFotoPerfilAtualizada(codIbge, usuarioId)
  }

  /**
   * Limpa todas as fotos de perfil armazenadas em cache.
   *
   * Deve ser chamado no logout.
   *
   * @returns {void}
   */
  limparCacheFotos() {
    const chaves = new Set([
      ...this.cacheFotos.keys(),
      ...this.requisicoesPendentes.keys(),
    ])

    chaves.forEach((chave) => {
      this.versoesCache.set(chave, (this.versoesCache.get(chave) || 0) + 1)
    })

    this.cacheFotos.forEach((url) => {
      this.liberarImagem(url)
    })

    this.cacheFotos.clear()
    this.requisicoesPendentes.clear()
  }

  /**
   * Libera uma URL temporária criada a partir de um Blob.
   *
   * Não deve ser utilizado diretamente pelos componentes
   * para liberar URLs pertencentes ao cache.
   *
   * @param {string|null} url
   * @returns {void}
   */
  liberarImagem(url) {
    if (typeof url === 'string' && url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  }

  /**
   * Registra um callback para alterações da foto de perfil.
   *
   * @param {Function} callback
   * @returns {Function} Função para cancelar a inscrição.
   */
  onFotoPerfilAtualizada(callback) {
    this.listenersFotoPerfil.add(callback)

    return () => {
      this.listenersFotoPerfil.delete(callback)
    }
  }

  /**
   * Notifica os componentes sobre uma alteração da foto.
   *
   * @param {string|number} codIbge
   * @param {string|number} usuarioId
   * @returns {void}
   */
  notificarFotoPerfilAtualizada(codIbge, usuarioId) {
    this.listenersFotoPerfil.forEach((callback) => {
      callback(codIbge, usuarioId)
    })
  }

  /**
   * Retorna a URL de um avatar padrão do DiceBear.
   *
   * @param {string} seed
   * @returns {string}
   */
  obterAvatarPadrao(seed = 'usuario') {
    return `https://api.dicebear.com/10.x/personas/svg?seed=${encodeURIComponent(
      seed
    )}&backgroundColor=e2e8f0&borderRadius=50`
  }
}

export default new ArquivosService()
